import { markRequest } from '../lib/actionFailureSignals';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

type ClientOption = { supabase?: SupabaseClient };
type SmartPricingRpcRow = Record<string, unknown>;

const SMART_PRICING_LOOKUP_BATCH_SIZE = 100;
const SMART_PRICING_LOOKUP_CONCURRENCY = 4;

export type SmartPricingSnapshotSummary = {
  snapshot_id: string;
  item_id: string;
  status: string;
  smart_unit_price: number | null;
  max_discount_pct: number | null;
};

export type SmartPricingEventApplyResult = {
  updated_count: number;
};

export type SmartPricingDiscountExceptionResult = {
  over_cap_line_count: number;
  approval_id: string | null;
};

export type LatestSmartPricingSnapshot = {
  snapshot_id: string;
  item_id: string;
  smart_unit_price: number | null;
  max_discount_pct: number | null;
  rationale: string | null;
  signals: Record<string, unknown>;
};

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asRows(data: unknown): SmartPricingRpcRow[] {
  return Array.isArray(data) ? data.filter((row): row is SmartPricingRpcRow => row !== null && typeof row === 'object') : [];
}

function firstRow(data: unknown): SmartPricingRpcRow | null {
  if (Array.isArray(data)) return asRows(data)[0] ?? null;
  return data !== null && typeof data === 'object' ? data as SmartPricingRpcRow : null;
}

export function isSmartPricingSchemaUnavailable(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as Partial<PostgrestError> & { status?: number };
  const code = typeof candidate.code === 'string' ? candidate.code : '';
  if (code === 'PGRST301' || code === 'PGRST302' || code === 'PGRST303') return false;
  if (['42P01', '42703', '42883', 'PGRST202', 'PGRST204', 'PGRST205'].includes(code)) return true;
  if (candidate.status === 404) return true;
  const message = [candidate.message, candidate.details, candidate.hint]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ')
    .toLowerCase();
  return (
    message.includes('smart_pricing') ||
    message.includes('smart pricing') ||
    message.includes('schema cache') ||
    message.includes('does not exist') ||
    message.includes('could not find the function')
  );
}

export async function generateQuarterlySmartPricingSnapshots(params: {
  locationId: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  approve?: boolean;
} & ClientOption): Promise<{ data: SmartPricingSnapshotSummary[]; error: PostgrestError | null; unavailable: boolean }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('generate_quarterly_smart_pricing_snapshots', {
    p_location_id: params.locationId,
    p_period_start: params.periodStart ?? undefined,
    p_period_end: params.periodEnd ?? undefined,
    p_approve: params.approve === true,
  }), 'mutation');
  if (error) {
    return { data: [], error, unavailable: isSmartPricingSchemaUnavailable(error) };
  }
  const rows = asRows(data);
  return {
    data: rows.map(row => ({
      snapshot_id: String(row.snapshot_id),
      item_id: String(row.item_id),
      status: String(row.status || ''),
      smart_unit_price: parseNumber(row.smart_unit_price),
      max_discount_pct: parseNumber(row.max_discount_pct),
    })),
    error: null,
    unavailable: false,
  };
}

export async function applyLatestSmartPricingToEvent(params: {
  eventId: string | null | undefined;
} & ClientOption): Promise<{ data: SmartPricingEventApplyResult | null; error: PostgrestError | null; unavailable: boolean }> {
  if (!params.eventId) return { data: null, error: null, unavailable: false };
  const supabase = params.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('apply_latest_smart_pricing_to_event', {
    p_event_id: params.eventId,
  }), 'mutation');
  if (error) {
    return { data: null, error, unavailable: isSmartPricingSchemaUnavailable(error) };
  }
  const row = firstRow(data);
  return {
    data: row ? { updated_count: parseNumber(row.updated_count) ?? 0 } : { updated_count: 0 },
    error: null,
    unavailable: false,
  };
}

export async function refreshSmartPricingDiscountException(params: {
  eventId: string | null | undefined;
} & ClientOption): Promise<{ data: SmartPricingDiscountExceptionResult | null; error: PostgrestError | null; unavailable: boolean }> {
  if (!params.eventId) return { data: null, error: null, unavailable: false };
  const supabase = params.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('refresh_smart_pricing_discount_exception', {
    p_event_id: params.eventId,
  }), 'mutation');
  if (error) {
    return { data: null, error, unavailable: isSmartPricingSchemaUnavailable(error) };
  }
  const row = firstRow(data);
  return {
    data: row
      ? {
          over_cap_line_count: parseNumber(row.over_cap_line_count) ?? 0,
          approval_id: row.approval_id ? String(row.approval_id) : null,
        }
      : { over_cap_line_count: 0, approval_id: null },
    error: null,
    unavailable: false,
  };
}

export async function fetchLatestApprovedSmartPricingSnapshots(params: {
  locationId: string | null | undefined;
  itemIds: Array<string | null | undefined>;
} & ClientOption): Promise<{ data: Map<string, LatestSmartPricingSnapshot>; error: PostgrestError | null; unavailable: boolean }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const locationId = params.locationId || null;
  const itemIds = Array.from(new Set((params.itemIds ?? []).map(id => (id == null ? '' : String(id).trim())).filter(Boolean)));
  const empty = new Map<string, LatestSmartPricingSnapshot>();
  if (!locationId || itemIds.length === 0) return { data: empty, error: null, unavailable: false };
  // PostgREST serializes `.in()` values into the URL. A full catalog can contain
  // thousands of item IDs, which exceeds practical URL limits and returns 400.
  // Keep the overlay lookup bounded while preserving one latest row per item.
  const batches: string[][] = [];
  for (let index = 0; index < itemIds.length; index += SMART_PRICING_LOOKUP_BATCH_SIZE) {
    batches.push(itemIds.slice(index, index + SMART_PRICING_LOOKUP_BATCH_SIZE));
  }
  const latest = new Map<string, LatestSmartPricingSnapshot>();
  let nextBatchIndex = 0;
  let firstError: PostgrestError | null = null;
  const fetchNextBatch = async (): Promise<void> => {
    while (!firstError) {
      const batchIndex = nextBatchIndex;
      nextBatchIndex += 1;
      const batch = batches[batchIndex];
      if (!batch) return;
      const { data, error } = await supabase
        .from('smart_pricing_snapshots')
        .select('snapshot_id,item_id,smart_unit_price,max_discount_pct,rationale,signals,approved_at,created_at')
        .eq('location_id', locationId)
        .eq('status', 'approved')
        .is('superseded_at', null)
        .in('item_id', batch)
        .order('approved_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });
      if (error) {
        firstError = error;
        return;
      }
      // Batches contain disjoint item IDs, so each worker can safely merge its
      // rows without changing the latest-row selection for another batch.
    asRows(data).forEach(row => {
      const itemId = row.item_id ? String(row.item_id) : '';
      if (!itemId || latest.has(itemId)) return;
      latest.set(itemId, {
        snapshot_id: String(row.snapshot_id),
        item_id: itemId,
        smart_unit_price: parseNumber(row.smart_unit_price),
        max_discount_pct: parseNumber(row.max_discount_pct),
        rationale: row.rationale ? String(row.rationale) : null,
        signals: row.signals && typeof row.signals === 'object' ? row.signals as Record<string, unknown> : {},
      });
    });
    }
  };
  await Promise.all(
    Array.from(
      { length: Math.min(SMART_PRICING_LOOKUP_CONCURRENCY, batches.length) },
      () => fetchNextBatch(),
    ),
  );
  if (firstError) {
    return { data: empty, error: firstError, unavailable: isSmartPricingSchemaUnavailable(firstError) };
  }
  return { data: latest, error: null, unavailable: false };
}
